import {
  TableBody,
  TableRow,
  Skeleton,
  Alert,
  AlertTitle,
} from "@mui/material";

import { RobotoTableCell } from "@/shared/components";

import {
  type EvaluationColumn,
  DEFAULT_EVALUATION_COLUMNS,
} from "./tableColumns";
import { TriggerEvaluationsRow } from "./TriggerEvaluationsRow";
import { useTriggerEvaluationsQuery } from "./useTriggerEvaluationsQuery";

interface TriggerEvaluationsTableBodyProps {
  columns?: EvaluationColumn[];
  page: number;
  rowsPerPage: number;
  evaluationsQuery: ReturnType<typeof useTriggerEvaluationsQuery>;
}

export function TriggerEvaluationsTableBody({
  columns,
  page,
  rowsPerPage,
  evaluationsQuery,
}: TriggerEvaluationsTableBodyProps) {
  const numColumns = columns?.length ?? DEFAULT_EVALUATION_COLUMNS.length;

  if (evaluationsQuery.isPending) {
    return (
      <TableBody>
        <TableRow>
          {Array.from({ length: numColumns }, (_, i) => (
            <RobotoTableCell key={`loading-cell-${i}`}>
              <Skeleton animation="pulse" variant="text" />
            </RobotoTableCell>
          ))}
        </TableRow>
      </TableBody>
    );
  }

  if (evaluationsQuery.error) {
    return (
      <TableBody>
        <TableRow>
          <RobotoTableCell colSpan={numColumns}>
            <Alert severity="error">
              <AlertTitle>Failed to fetch trigger evaluations</AlertTitle>
              Please contact{" "}
              <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
              problem persists.
            </Alert>
          </RobotoTableCell>
        </TableRow>
      </TableBody>
    );
  }

  const allEvaluations = evaluationsQuery.data ?? [];
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  let pageEvaluations = allEvaluations.slice(startIndex, endIndex);

  // If we're fetching the next page and don't have data for the current page yet,
  // show the previous page's data to avoid an empty table
  if (allEvaluations.length > 0 && pageEvaluations.length === 0) {
    const previousStartIndex = Math.max(0, startIndex - rowsPerPage);
    pageEvaluations = allEvaluations.slice(previousStartIndex, startIndex);
  }

  if (pageEvaluations.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <RobotoTableCell colSpan={numColumns}>
            <Alert severity="info">No trigger evaluations yet.</Alert>
          </RobotoTableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {pageEvaluations.map((evaluation) => (
        <TriggerEvaluationsRow
          key={evaluation.trigger_evaluation_id}
          columns={columns}
          evaluation={evaluation}
        />
      ))}
    </TableBody>
  );
}
